// WaitlistPanel.js - Queue list with party cards and actions
import { CheckCircle, Clock, MapPin, Phone, Plus, Trash2, UserCheck, Users, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import { getAllSites } from '../SiteManagement/siteApi';
import IconTextButton from '../ui/IconTextButton';
import PrimaryButton from '../ui/PrimaryButton';
import Button from '../ui/button';
import Input from '../ui/input';
import { tableBookingApi } from './tableBookingApi';

const WaitlistPanel = () => {
  // Log screen view
  useScreenLogging('WaitlistPanel', {
    component: 'WaitlistPanel',
    screenType: 'waitlist',
  });

  // Dummy data for development/testing - COMMENTED OUT: Now using API calls
  // const dummySites = [
  //   { site_id: '1', site_name: 'Downtown Restaurant' },
  //   { site_id: '2', site_name: 'Mall Location' },
  //   { site_id: '3', site_name: 'Airport Branch' },
  // ];

  // const dummyWaitlist = [
  //   {
  //     waitlist_id: '1',
  //     guest_name: 'John Smith',
  //     contact_phone: '+1 (555) 123-4567',
  //     party_size: 4,
  //     estimated_arrival: new Date(Date.now() + 15 * 60000).toISOString(),
  //     wait_time_minutes: 25,
  //     notify_status: 'pending',
  //     notes: 'Window seat preferred',
  //     guest_contact_verified: true,
  //     status: 'waiting',
  //   },
  //   {
  //     waitlist_id: '2',
  //     guest_name: 'Sarah Johnson',
  //     contact_phone: '+1 (555) 234-5678',
  //     party_size: 2,
  //     estimated_arrival: new Date(Date.now() + 30 * 60000).toISOString(),
  //     wait_time_minutes: 15,
  //     notify_status: 'sent',
  //     notes: 'Anniversary dinner',
  //     guest_contact_verified: true,
  //     status: 'waiting',
  //   },
  //   {
  //     waitlist_id: '3',
  //     guest_name: 'Michael Chen',
  //     contact_phone: '+1 (555) 345-6789',
  //     party_size: 6,
  //     estimated_arrival: new Date(Date.now() + 45 * 60000).toISOString(),
  //     wait_time_minutes: 40,
  //     notify_status: 'confirmed',
  //     notes: 'Large group, need round table',
  //     guest_contact_verified: false,
  //     status: 'waiting',
  //   },
  //   {
  //     waitlist_id: '4',
  //     guest_name: 'Emily Davis',
  //     contact_phone: '+1 (555) 456-7890',
  //     party_size: 3,
  //     estimated_arrival: new Date(Date.now() - 20 * 60000).toISOString(),
  //     wait_time_minutes: 0,
  //     notify_status: 'sent',
  //     notes: '',
  //     guest_contact_verified: true,
  //     status: 'seated',
  //     seated_duration_minutes: 20,
  //   },
  //   {
  //     waitlist_id: '5',
  //     guest_name: 'Robert Wilson',
  //     contact_phone: '+1 (555) 567-8901',
  //     party_size: 2,
  //     estimated_arrival: new Date(Date.now() + 10 * 60000).toISOString(),
  //     wait_time_minutes: 5,
  //     notify_status: 'pending',
  //     notes: 'Vegetarian options needed',
  //     guest_contact_verified: false,
  //     status: 'waiting',
  //   },
  //   {
  //     waitlist_id: '6',
  //     guest_name: 'Lisa Anderson',
  //     contact_phone: '+1 (555) 678-9012',
  //     party_size: 5,
  //     estimated_arrival: new Date(Date.now() + 60 * 60000).toISOString(),
  //     wait_time_minutes: 50,
  //     notify_status: 'pending',
  //     notes: 'Birthday celebration',
  //     guest_contact_verified: true,
  //     status: 'waiting',
  //   },
  // ];

  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [waitlist, setWaitlist] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Summary stats
  const [summary, setSummary] = useState({
    totalWaiting: 0,
    averageWaitTime: 0,
    averageSeatedTime: 0,
  });

  // Add to waitlist form (fields match API: site_id, party_size, guest_name, guest_contact, eta_minutes, notify_status, notes)
  const [showAddForm, setShowAddForm] = useState(false);
  const [newParty, setNewParty] = useState({
    guest_name: '',
    guest_contact: '',
    party_size: 2,
    eta_minutes: 15,
    notify_status: 'pending',
    notes: '',
  });
  const initialNewParty = {
    guest_name: '',
    guest_contact: '',
    party_size: 2,
    eta_minutes: 15,
    notify_status: 'pending',
    notes: '',
  };

  // Load sites on mount
  useEffect(() => {
    loadSites();
  }, []);

  // Load waitlist when site changes
  useEffect(() => {
    if (selectedSiteId) {
      loadWaitlist();
      // Refresh every 30 seconds
      const interval = setInterval(loadWaitlist, 30000);
      return () => clearInterval(interval);
    } else {
      // Clear waitlist if no site selected
      setWaitlist([]);
    }
  }, [selectedSiteId]);

  // Calculate summary stats
  useEffect(() => {
    if (waitlist.length > 0) {
      const totalWaiting = waitlist.length;
      // Use eta_minutes from API response
      const averageWaitTime = waitlist.reduce((sum, entry) => {
        const waitTime = entry.eta_minutes || 0;
        return sum + waitTime;
      }, 0) / totalWaiting;
      
      // Calculate wait time from created_at if available
      const averageSeatedTime = 0; // API doesn't provide seated time in waitlist entries

      setSummary({
        totalWaiting,
        averageWaitTime: Math.round(averageWaitTime),
        averageSeatedTime: Math.round(averageSeatedTime),
      });
    } else {
      setSummary({ totalWaiting: 0, averageWaitTime: 0, averageSeatedTime: 0 });
    }
  }, [waitlist]);

  const loadSites = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getAllSites();
      const sitesData = Array.isArray(response) 
        ? response 
        : (response?.sites || response?.data || []);
      // Sort sites alphabetically by name
      const sortedSites = [...sitesData].sort((a, b) => {
        const nameA = (a.site_name || a.name || 'Unknown Site').toLowerCase();
        const nameB = (b.site_name || b.name || 'Unknown Site').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      setSites(sortedSites);
      
      // Auto-select first site in alphabetical order if available and none selected
      if (sortedSites.length > 0 && !selectedSiteId) {
        const firstSite = sortedSites[0];
        setSelectedSiteId(firstSite.site_id || firstSite.id || '');
      }
      
      // Log successful API call
      loggingService.logInfo(
        'waitlist_load_sites',
        `Successfully loaded ${sortedSites.length} sites for waitlist`,
        {
          endpoint: '/sites',
          method: 'GET',
          siteCount: sortedSites.length,
        }
      ).catch(logError => {
        console.warn('[WaitlistPanel] Failed to log event:', logError);
      });
    } catch (err) {
      console.error('Error loading sites:', err);
      const errorMessage = err.message || 'Failed to load sites';
      setError(errorMessage);
      // Fallback to empty array on error
      setSites([]);
      
      // Log API error
      loggingService.logError(
        'waitlist_load_sites_error',
        `Failed to load sites for waitlist: ${errorMessage}`,
        err,
        {
          endpoint: '/sites',
          method: 'GET',
        }
      ).catch(logError => {
        console.warn('[WaitlistPanel] Failed to log error:', logError);
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadWaitlist = async () => {
    if (!selectedSiteId) {
      setWaitlist([]);
      return;
    }
    try {
      setIsLoading(true);
      const waitlistData = await tableBookingApi.getWaitlist({ site_id: selectedSiteId });
      console.log('List Waitlist response:', waitlistData);
      const formattedData = Array.isArray(waitlistData) ? waitlistData : [];
      setWaitlist(formattedData);
      setError(null);
      
      // Log successful API call
      loggingService.logInfo(
        'waitlist_load',
        `Successfully loaded ${formattedData.length} waitlist entries for site ${selectedSiteId}`,
        {
          endpoint: '/tables/waitlist',
          method: 'GET',
          siteId: selectedSiteId,
          waitlistCount: formattedData.length,
        }
      ).catch(logError => {
        console.warn('[WaitlistPanel] Failed to log event:', logError);
      });
    } catch (err) {
      console.error('Error loading waitlist:', err);
      setWaitlist([]);
      setError(err.message || 'Failed to load waitlist');
      
      // Log API error
      loggingService.logError(
        'waitlist_load_error',
        `Failed to load waitlist for site ${selectedSiteId}: ${err.message || 'Unknown error'}`,
        err,
        {
          endpoint: '/tables/waitlist',
          method: 'GET',
          siteId: selectedSiteId,
        }
      ).catch(logError => {
        console.warn('[WaitlistPanel] Failed to log error:', logError);
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToWaitlist = async () => {
    if (!selectedSiteId) {
      setError('Please select a site first');
      return;
    }

    if (!newParty.guest_name?.trim()) {
      setError('Please provide guest name');
      return;
    }

    try {
      setIsLoading(true);

      // Request body per API: site_id, party_size, guest_name, guest_contact, eta_minutes, notify_status, notes
      const waitlistData = {
        site_id: selectedSiteId,
        party_size: Number(newParty.party_size) || 2,
        guest_name: newParty.guest_name.trim(),
        guest_contact: newParty.guest_contact?.trim() || undefined,
        eta_minutes: Number(newParty.eta_minutes) >= 0 ? Number(newParty.eta_minutes) : 15,
        notify_status: newParty.notify_status || 'pending',
        notes: newParty.notes?.trim() || undefined,
      };

      const createResponse = await tableBookingApi.createWaitlistEntry(waitlistData);
      console.log('Create Waitlist Entry response:', createResponse);
      const waitlistId = createResponse?.waitlist_id || createResponse?.id;
      
      // Log successful API call
      loggingService.logInfo(
        'waitlist_create_entry',
        `Successfully created waitlist entry ${waitlistId} for site ${selectedSiteId}`,
        {
          endpoint: '/tables/waitlist',
          method: 'POST',
          siteId: selectedSiteId,
          waitlistId: waitlistId,
          partySize: waitlistData.party_size,
          guestName: waitlistData.guest_name,
        }
      ).catch(logError => {
        console.warn('[WaitlistPanel] Failed to log event:', logError);
      });
      
      await loadWaitlist();
      setNewParty(initialNewParty);
      setShowAddForm(false);
      setError(null);
    } catch (err) {
      console.error('Error adding to waitlist:', err);
      setError(err.message || 'Failed to add party to waitlist');
      
      // Log API error
      loggingService.logError(
        'waitlist_create_entry_error',
        `Failed to create waitlist entry for site ${selectedSiteId}: ${err.message || 'Unknown error'}`,
        err,
        {
          endpoint: '/tables/waitlist',
          method: 'POST',
          siteId: selectedSiteId,
          partySize: waitlistData.party_size,
        }
      ).catch(logError => {
        console.warn('[WaitlistPanel] Failed to log error:', logError);
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleNotify = async (waitlistId) => {
    try {
      setIsLoading(true);
      await tableBookingApi.updateWaitlistEntry(waitlistId, { notify_status: 'notified' });
      
      // Log successful API call
      loggingService.logInfo(
        'waitlist_notify',
        `Successfully notified waitlist entry ${waitlistId}`,
        {
          endpoint: `/tables/waitlist/${waitlistId}`,
          method: 'PUT',
          waitlistId: waitlistId,
          action: 'notify',
        }
      ).catch(logError => {
        console.warn('[WaitlistPanel] Failed to log event:', logError);
      });
      
      await loadWaitlist();
      setError(null);
    } catch (err) {
      console.error('Error notifying party:', err);
      setError('Failed to send notification');
      
      // Log API error
      loggingService.logError(
        'waitlist_notify_error',
        `Failed to notify waitlist entry ${waitlistId}: ${err.message || 'Unknown error'}`,
        err,
        {
          endpoint: `/tables/waitlist/${waitlistId}`,
          method: 'PUT',
          waitlistId: waitlistId,
        }
      ).catch(logError => {
        console.warn('[WaitlistPanel] Failed to log error:', logError);
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSeat = async (waitlistId) => {
    try {
      setIsLoading(true);
      const response = await tableBookingApi.updateWaitlistEntry(waitlistId, { notify_status: 'seated' });
      console.log('Update Waitlist Entry (seat) response:', response);
      
      // Log successful API call
      loggingService.logInfo(
        'waitlist_seat',
        `Successfully seated waitlist entry ${waitlistId}`,
        {
          endpoint: `/tables/waitlist/${waitlistId}`,
          method: 'PUT',
          waitlistId: waitlistId,
          action: 'seat',
        }
      ).catch(logError => {
        console.warn('[WaitlistPanel] Failed to log event:', logError);
      });
      
      await loadWaitlist();
      setError(null);
    } catch (err) {
      console.error('Error seating party:', err);
      setError('Failed to seat party');
      
      // Log API error
      loggingService.logError(
        'waitlist_seat_error',
        `Failed to seat waitlist entry ${waitlistId}: ${err.message || 'Unknown error'}`,
        err,
        {
          endpoint: `/tables/waitlist/${waitlistId}`,
          method: 'PUT',
          waitlistId: waitlistId,
        }
      ).catch(logError => {
        console.warn('[WaitlistPanel] Failed to log error:', logError);
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemove = async (waitlistId) => {
    if (!window.confirm('Are you sure you want to remove this party from the waitlist?')) {
      return;
    }
    try {
      setIsLoading(true);
      const response = await tableBookingApi.deleteWaitlistEntry(waitlistId);
      console.log('Delete Waitlist Entry response:', response);
      
      // Log successful API call
      loggingService.logInfo(
        'waitlist_remove',
        `Successfully removed waitlist entry ${waitlistId}`,
        {
          endpoint: `/tables/waitlist/${waitlistId}`,
          method: 'DELETE',
          waitlistId: waitlistId,
          action: 'remove',
        }
      ).catch(logError => {
        console.warn('[WaitlistPanel] Failed to log event:', logError);
      });
      
      await loadWaitlist();
      setError(null);
    } catch (err) {
      console.error('Error removing from waitlist:', err);
      setError('Failed to remove party');
      
      // Log API error
      loggingService.logError(
        'waitlist_remove_error',
        `Failed to remove waitlist entry ${waitlistId}: ${err.message || 'Unknown error'}`,
        err,
        {
          endpoint: `/tables/waitlist/${waitlistId}`,
          method: 'DELETE',
          waitlistId: waitlistId,
        }
      ).catch(logError => {
        console.warn('[WaitlistPanel] Failed to log error:', logError);
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelay = async (waitlistId, minutes = 10) => {
    try {
      setIsLoading(true);
      const entry = await tableBookingApi.getWaitlistEntry(waitlistId);
      console.log('Get Waitlist Entry response:', entry);
      const currentEta = entry.eta_minutes ?? 0;
      const updateResponse = await tableBookingApi.updateWaitlistEntry(waitlistId, { eta_minutes: currentEta + minutes });
      console.log('Update Waitlist Entry (delay) response:', updateResponse);
      
      // Log successful API call
      loggingService.logInfo(
        'waitlist_delay',
        `Successfully delayed waitlist entry ${waitlistId} by ${minutes} minutes`,
        {
          endpoint: `/tables/waitlist/${waitlistId}`,
          method: 'PUT',
          waitlistId: waitlistId,
          action: 'delay',
          delayMinutes: minutes,
          newEtaMinutes: currentEta + minutes,
        }
      ).catch(logError => {
        console.warn('[WaitlistPanel] Failed to log event:', logError);
      });
      
      await loadWaitlist();
      setError(null);
    } catch (err) {
      console.error('Error delaying party:', err);
      setError('Failed to delay party');
      
      // Log API error
      loggingService.logError(
        'waitlist_delay_error',
        `Failed to delay waitlist entry ${waitlistId}: ${err.message || 'Unknown error'}`,
        err,
        {
          endpoint: `/tables/waitlist/${waitlistId}`,
          method: 'PUT',
          waitlistId: waitlistId,
          delayMinutes: minutes,
        }
      ).catch(logError => {
        console.warn('[WaitlistPanel] Failed to log error:', logError);
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getNotifyStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-gray-100 text-gray-700 border-gray-300';
      case 'notified':
      case 'sent':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'seated':
      case 'confirmed':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'left':
        return 'bg-orange-100 text-orange-700 border-orange-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const formatETA = (entry) => {
    // Handle API response format: calculate ETA from created_at + eta_minutes
    if (entry?.created_at && entry?.eta_minutes !== undefined) {
      const createdDate = new Date(entry.created_at);
      const etaDate = new Date(createdDate.getTime() + entry.eta_minutes * 60000);
      return etaDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    // Fallback for legacy format
    if (entry?.estimated_arrival) {
      const date = new Date(entry.estimated_arrival);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return 'N/A';
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-900">Waitlist Management</h1>
          <IconTextButton
            icon={<Plus className="w-7 h-7" />}
            text="Add Party"
            onClick={() => setShowAddForm(!showAddForm)}
          />
        </div>

        {/* Site Selector */}
        <div className="flex items-center gap-4">
          <div className="min-w-[200px]">
            <label className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
              <MapPin className="w-4 h-4" />
              Select Site
            </label>
            <select
              value={selectedSiteId}
              onChange={(e) => setSelectedSiteId(e.target.value)}
              className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
              disabled={isLoading}
            >
              {/* <option value="">Select a site</option> */}
              {/* alphabetical order */}
              {Array.isArray(sites) && sites.map((site) => (
                <option key={site.site_id || site.id} value={site.site_id || site.id}>
                  {site.site_name || site.name || 'Unknown Site'}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Summary Band */}
        {selectedSiteId && (
          <div className="mt-4 grid grid-cols-3 gap-4 mb-2">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-sm text-blue-600 font-medium">Parties Waiting</div>
              <div className="text-2xl font-bold text-blue-900">{summary.totalWaiting}</div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="text-sm text-yellow-600 font-medium">Avg Wait Time</div>
              <div className="text-2xl font-bold text-yellow-900">{summary.averageWaitTime} min</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="text-sm text-green-600 font-medium">Avg Seated Time</div>
              <div className="text-2xl font-bold text-green-900">{summary.averageSeatedTime} min</div>
            </div>
          </div>
        )}

        {/* Add Party Form Overlay */}
        {showAddForm && createPortal(
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowAddForm(false);
              setNewParty(initialNewParty);
            }}
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
                <h3 className="text-2xl font-bold text-gray-900">Add Party to Waitlist</h3>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewParty(initialNewParty);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-600" />
                </button>
              </div>

              {/* Form Content - matches API: guest_name, guest_contact, party_size, eta_minutes, notify_status, notes */}
              <div className="p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Guest Name *</label>
                    <Input
                      type="text"
                      value={newParty.guest_name}
                      onChange={(e) => setNewParty({ ...newParty, guest_name: e.target.value })}
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Guest Contact</label>
                    <Input
                      type="tel"
                      value={newParty.guest_contact}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const allowed = raw.replace(/[^\d+\s]/g, '');
                        setNewParty({ ...newParty, guest_contact: allowed });
                      }}
                      placeholder="+91XXXXXXXXXX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Party Size</label>
                    <Input
                      type="number"
                      min={1}
                      value={newParty.party_size}
                      onChange={(e) => setNewParty({ ...newParty, party_size: parseInt(e.target.value, 10) || 2 })}
                      placeholder="2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ETA (minutes)</label>
                    <Input
                      type="number"
                      min={0}
                      value={newParty.eta_minutes}
                      onChange={(e) => setNewParty({ ...newParty, eta_minutes: parseInt(e.target.value, 10) })}
                      placeholder="15"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notify Status</label>
                    <select
                      value={newParty.notify_status}
                      onChange={(e) => setNewParty({ ...newParty, notify_status: e.target.value })}
                      className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                    >
                      <option value="pending">Pending</option>
                      {/* <option value="notified">Notified</option> */}
                      <option value="seated">Seated</option>
                      <option value="left">Left</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={newParty.notes}
                      onChange={(e) => setNewParty({ ...newParty, notes: e.target.value })}
                      className="w-full min-h-[80px] rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                      placeholder="Prefers outdoor seating"
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-6 justify-center">
                  <PrimaryButton 
                    onClick={handleAddToWaitlist} 
                    disabled={isLoading}
                    className="w-auto"
                  >
                    Add to Waitlist
                  </PrimaryButton>
                  {/* <Button
                    onClick={() => { setShowAddForm(false); setNewParty(initialNewParty); }}
                    className="bg-red-500 hover:bg-red-600 text-white"
                  >
                    Cancel
                  </Button> */}
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Waitlist Queue */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && waitlist.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-500">Loading waitlist...</div>
          </div>
        ) : waitlist.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium">No parties in waitlist</p>
              <p className="text-sm">Add a party to get started</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {waitlist.map((entry) => (
              <div
                key={entry.waitlist_id}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {entry.guest_name || 'Guest'}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded border ${getNotifyStatusColor(entry.notify_status || 'pending')}`}>
                        {entry.notify_status || 'pending'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        <span>Party of {entry.party_size}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        <span>{entry.guest_contact || entry.contact_phone || 'N/A'}</span>
                        {entry.guest_contact_verified && (
                          <UserCheck className="w-4 h-4 text-green-500" title="Verified" />
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>ETA: {formatETA(entry)}</span>
                      </div>
                      {entry.eta_minutes !== undefined && (
                        <div className="flex items-center gap-2">
                          <span>Arriving in: {entry.eta_minutes} min</span>
                        </div>
                      )}
                    </div>
                    {entry.notes && (
                      <div className="mt-2 text-sm text-gray-500 italic">
                        Notes: {entry.notes}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 ml-4">
                    {/* Notify button hidden as per requirements */}
                    {/* <Button
                      onClick={() => handleNotify(entry.waitlist_id)}
                      className="text-xs px-3 py-1 flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white"
                      disabled={entry.notify_status === 'confirmed'}
                    >
                      <Bell className="w-3 h-3 text-white" />
                      Notify
                    </Button> */}
                    <Button
                      onClick={() => handleSeat(entry.waitlist_id)}
                      className="text-xs px-3 py-1 flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white"
                    >
                      <CheckCircle className="w-3 h-3 text-white" />
                      Seat
                    </Button>
                    <Button
                      onClick={() => handleDelay(entry.waitlist_id, 10)}
                      className="text-xs px-3 py-1 flex items-center gap-1 bg-yellow-500 hover:bg-yellow-600 text-white"
                    >
                      <Clock className="w-3 h-3 text-white" />
                      Delay 10m
                    </Button>
                    <Button
                      onClick={() => handleRemove(entry.waitlist_id)}
                      className="text-xs px-3 py-1 flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white"
                    >
                      <Trash2 className="w-3 h-3 text-white" />
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WaitlistPanel;
